import type { Role } from "."

export interface ScopeMetadata {
  id: string
  name: string
}

export interface Permissions {
  areAllScopesPermitted: boolean
  specificScopes: ScopeMetadata[]
}

export interface UserEntity {
  id: string
  email: string
  role?: Role
  firstName?: string
  lastName?: string
  jobDescription?: string
  lastLogin?: string
  status?: Status
  company?: string
  key: string
  initials?: string
  colorNumber?: number
  permissions?: Permissions
}

export interface User extends Omit<UserEntity, "key"> {
  initials: string
  company: string
  authorized: boolean
  featureFlags: Features.Flags
  settings: Settings.Shared
  photoURL?: string
  userNeedVerificationCodeMultiFactor?: boolean
  userNeedVerifyPhoneNumberMultiFactor?: boolean
  currentScopeId?: string | null
  fullName?: string
}

export type Status = "ACCEPTED" | "INVITED" | "DECLINED"

export const getColorNumberForUserAvatar = (email: string) => {
  return (email.length % 6) + 1
}

export const isKnownUserRole = (user: User) => !!user.role

export const isUserAdmin = (user: User) => user.role === "ADMIN"

export const isAuthorizedUser = (user: User) => !!user.authorized

export const isUserVerified = (user: User) =>
  !!user.email && isAuthorizedUser(user)

export const isFullNameDefined = (user: User) => user.fullName !== undefined

export const hasFullName = (user: User) => !!user.fullName

export const createUser = (values: Partial<User> = {}): User => ({
  id: "",
  initials: "",
  email: "",
  company: "",
  authorized: false,
  fullName: undefined,
  featureFlags: {
    dataReadiness: false,
    internetFacingIntegration: undefined,
    kortexInstalled: undefined,
    scmInstalled: undefined,
    showOnboarding: undefined,
    sastScanFeature: undefined,
    isWelcomeDismissed: undefined,
    vmInstancesEnabled: undefined,
    repoConfigEnabled: undefined,
    ciIntegrated: undefined,
    trialDays: undefined,
    adrIncidentsEnabled: undefined,
    chatbotEnabled: undefined,
    isInternalTenant: undefined,
    multiLanguage: undefined,
    devMode: undefined,
    aiDiscovery: undefined,
    aiPolicies: undefined
  },
  settings: {
    packagesExportLimits: 200000
  },
  ...values
})

export const getInitials = (fullNameOrEmail: string) => {
  try {
    if (!fullNameOrEmail) return "?"

    let str = String(fullNameOrEmail).trim()

    if (!str) return "?"

    const at = str.indexOf("@")

    if (at > 0) str = str.slice(0, at)
    else if (at === 0) str = str.slice(1)

    const lastSegment = str.split("/").pop()

    if (!lastSegment) return "?"

    const tokens = lastSegment
      .replace(/([a-z\d])([A-Z])/g, "$1 $2")
      .split(/[\s._\-+]+/)
      .filter(Boolean)

    if (tokens.length === 0) return "?"

    const initials =
      tokens.length === 1
        ? tokens[0][0]
        : tokens[0][0] + tokens[tokens.length - 1][0]

    return initials.toUpperCase()
  } catch {
    return "?"
  }
}

export const isKortexInstalled = (user: User) =>
  !!user?.featureFlags?.kortexInstalled

export const isSensorInstalled = (user: User) =>
  !!user?.featureFlags?.scmInstalled

export const noScopeSelected = (user: User) => user.currentScopeId === null

export const isFeatureFlagMaybe = (value: unknown) => value === undefined

// Live reads real inventory data; mock reads the demo backend, which has no MCP servers.
export const isAiDiscoveryLive = (mode: Features.Flags["aiDiscovery"]) =>
  mode === "live"

// kosmos-api decides who sees AI Discovery and where its data comes from (live or the demo mock)
// The AI policies of a connected agent platform, apart from the demo ones
export const isLiveAiPoliciesEnabled = (
  mode: Features.Flags["aiDiscovery"],
  aiPolicies: Features.Flags["aiPolicies"]
) => !!aiPolicies && isAiDiscoveryLive(mode)

export const isAiDiscoveryMock = (mode: Features.Flags["aiDiscovery"]) =>
  mode === "mock"

export const isAiDiscoveryEnabled = (mode: Features.Flags["aiDiscovery"]) =>
  isAiDiscoveryLive(mode) || isAiDiscoveryMock(mode)

export const getLanguage = (
  slug: string | null | undefined
): Settings.Language => (slug === "deepcheck" ? "ch" : "en")
